// ============================================================================
// PsyAssignDialog — назначение методики пациенту и выдача ссылки/QR/кода.
//
// Ссылку создаёт СЕРВЕР (CreatePollGuestLink): она привязана к методике и
// пациенту, активная ссылка переиспользуется (без дублей). Пациент проходит
// без входа (публичная страница /poll/{база}/{токен}); короткий код — первые
// 8 символов токена. У внешней методики (0 вопросов) ссылка не нужна —
// результат вносится вручную.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { ExternalLink, Loader2, QrCode, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/CopyButton';
import { useToastStore } from '@/store/toastStore';
import { getSDK } from '@/store/sdkStore';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import {
  activeLink, ensureAssignment, methodQuestionCount, pollLink, saveAssignmentLink,
  shortCode, type PsyAssignment, type PsyMethod,
} from '@/lib/psyWorkspace';

interface Props {
  patientId: string;
  patientName: string;
  method: PsyMethod;
  assignment: PsyAssignment | null;
  onClose: () => void;
  onManual: () => void;
}

interface LinkState {
  token: string;
  url: string;
  code: string;
  qr: string;
  expiresAt: string;
}

export function PsyAssignDialog({ patientId, patientName, method, assignment, onClose, onManual }: Props) {
  const showError = useToastStore((s) => s.showError);
  const [checking, setChecking] = useState(true);
  const [manualOnly, setManualOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [link, setLink] = useState<LinkState | null>(null);
  const existingLink = activeLink(assignment);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const count = await methodQuestionCount(method);
        if (!cancelled) setManualOnly(count === 0);
      } catch (e) {
        logErrorToJournal(e, `Психолог: вопросы методики ${method.code}`);
        if (!cancelled) setError(friendlyError(e));
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [method]);

  const createLink = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const a = await ensureAssignment(patientId, patientName, method, assignment ? [assignment] : []);
      const r = await getSDK().poll.createPollGuestLink({
        pollCode: method.code,
        subjectId: patientId,
        ttlMinutes: 1440,
        origin: typeof window !== 'undefined' ? window.location.origin : '',
      });
      await saveAssignmentLink(a.id, r.token, r.expiresAt);
      setLink({
        token: r.token,
        url: r.url || pollLink(typeof window !== 'undefined' ? window.location.origin : '', r.token),
        code: shortCode(r.token).toUpperCase(),
        qr: r.qrPngBase64,
        expiresAt: r.expiresAt,
      });
    } catch (e) {
      logErrorToJournal(e, `Психолог: ссылка методики ${method.code}`);
      setError(friendlyError(e));
      showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }, [assignment, method, patientId, patientName, showError]);

  return (
    <div className="fixed inset-0 z-[12000] bg-black/50 flex items-center justify-center p-4" onMouseDown={onClose}>
      <div className="bg-card rounded-xl shadow-2xl w-[min(560px,96vw)] max-h-[90vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center gap-2">
          <Share2 className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Назначение: {method.synonym}</span>
          <Button size="sm" variant="ghost" className="h-7 px-2 ml-auto text-[11px]" onClick={onClose}>Закрыть</Button>
        </div>
        <div className="p-4 space-y-3 overflow-auto">
          <div className="text-xs text-muted-foreground">Пациент: <span className="text-foreground">{patientName}</span></div>
          {error && <div className="text-xs text-destructive border border-destructive/40 rounded px-2 py-1.5">{error}</div>}

          {checking && <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />Проверяем методику…</div>}

          {!checking && manualOnly && (
            <div className="space-y-3">
              <div className="text-sm">У методики нет онлайн-вопросов (внешняя/авторская).</div>
              <div className="text-xs text-muted-foreground">
                Ссылка пациенту не требуется: результат (балл и/или заключение) вносит специалист.
              </div>
              <Button size="sm" className="h-7 text-[11px]" onClick={onManual}>Внести результат вручную</Button>
            </div>
          )}

          {!checking && !manualOnly && !link && (
            <div className="space-y-3">
              <div className="text-sm">Пациент проходит тест по ссылке, QR или короткому коду.</div>
              {existingLink && (
                <div className="text-[11px] text-muted-foreground">
                  Ссылка уже создана (действует до {existingLink.expiresAt ? new Date(existingLink.expiresAt).toLocaleString('ru-RU') : '—'}).
                  Покажем её без создания дубля.
                </div>
              )}
              <Button size="sm" className="h-7 text-[11px]" onClick={() => void createLink()} disabled={busy}>
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <QrCode className="h-3.5 w-3.5 mr-1" />}
                {existingLink ? 'Показать ссылку и QR' : 'Назначить и создать ссылку'}
              </Button>
            </div>
          )}

          {link && (
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-4">
                {link.qr && (
                  <img src={`data:image/png;base64,${link.qr}`} alt="QR для прохождения теста"
                    className="w-40 h-40 border rounded bg-white p-1 shrink-0" />
                )}
                <div className="min-w-0 space-y-2 text-xs">
                  <div>
                    <div className="text-[11px] text-muted-foreground mb-0.5">Ссылка пациенту</div>
                    <div className="flex items-center gap-1 min-w-0">
                      <span className="truncate border rounded px-1.5 py-1 flex-1 bg-muted/30">{link.url}</span>
                      <CopyButton text={link.url} compact />
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] text-muted-foreground mb-0.5">Короткий код</div>
                    <div className="flex items-center gap-1">
                      <span className="font-mono text-base tracking-wider border rounded px-2 py-1 bg-muted/30">{link.code}</span>
                      <CopyButton text={link.code} compact />
                    </div>
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Действует до {link.expiresAt ? new Date(link.expiresAt).toLocaleString('ru-RU') : '—'}.
                    Пациент вводит код на странице прохождения (или открывает ссылку/QR).
                  </div>
                  <a href={link.url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-primary text-[11px] hover:underline">
                    <ExternalLink className="h-3 w-3" />Открыть страницу прохождения
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="px-4 py-2.5 border-t flex justify-end gap-2">
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={onClose}>Готово</Button>
        </div>
      </div>
    </div>
  );
}
